import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, ApiError, loadStoredToken, setAccessToken } from './api.js';

interface MePayload {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    organizationId: string;
    nmls?: string | null;
  };
  modulePermissions: Array<{
    moduleKey: string;
    label: string;
    phase1Enabled: boolean;
    canView: boolean;
    canEdit: boolean;
    canAdmin: boolean;
  }>;
}

export interface SignUpPendingResult {
  pending: true;
  email: string;
  message: string;
  /** Whether the verification email actually went out (false = SMTP failed/absent). */
  delivered?: boolean;
  /** True when the account is held for manual admin NMLS review (no email sent). */
  nmlsReview?: boolean;
  /** Only set in dev mode (no SMTP). Click to bypass real email. */
  devVerificationUrl?: string;
}

interface AuthContextValue {
  loading: boolean;
  me: MePayload | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: {
    organizationName: string;
    name: string;
    email: string;
    password: string;
    nmls?: string;
  }) => Promise<SignUpPendingResult>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<MePayload | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = loadStoredToken();
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api<MePayload>('/api/me');
      setMe(data);
    } catch (err) {
      // Only sign the user out when the token is actually invalid (401).
      // Network errors, 5xx, CORS hiccups, or a temporarily-down API should
      // NOT clear the token — otherwise a single backend crash kicks every
      // tab back to the sign-in screen.
      if (err instanceof ApiError && err.status === 401) {
        setAccessToken(null);
        setMe(null);
      }
      // For other errors, keep the cached me (or leave null) and let the
      // next request retry. Surface a console warning for visibility.
      else {
        console.warn('[auth] /api/me failed (not a 401, keeping token):', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const signIn = async (email: string, password: string) => {
    const data = await api<{ accessToken: string }>('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setAccessToken(data.accessToken);
    await refresh();
  };

  const signUp: AuthContextValue['signUp'] = async (input) => {
    // Signup now returns { pending: true, email, message, devVerificationUrl? }.
    // The user is created with status='pending_verification' and must click
    // the link in the welcome email to activate. We do NOT auto-sign-in.
    const data = await api<SignUpPendingResult>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data;
  };

  const signOut = () => {
    setAccessToken(null);
    setMe(null);
  };

  return (
    <AuthContext.Provider value={{ loading, me, signIn, signUp, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthProvider missing');
  return ctx;
}

export function useModulePermission(moduleKey: string): {
  canView: boolean;
  canEdit: boolean;
  canAdmin: boolean;
} {
  const { me } = useAuth();
  const perm = me?.modulePermissions.find((p) => p.moduleKey === moduleKey);
  return {
    canView: !!perm?.canView,
    canEdit: !!perm?.canEdit,
    canAdmin: !!perm?.canAdmin,
  };
}
